class UpsellCart extends HTMLElement {
  constructor() {
    super();
    this._connected = false;
    this._controller = null;
    this._container = null;
  }

  connectedCallback() {
    if (this._connected) return;
    this._connected = true;
    this._controller = new AbortController();

    this.innerHTML = "";
    this._container = document.createElement("div");
    this._container.className = "upsell-cart-list";
    this.appendChild(this._container);

    const products = this._parseProducts();
    if (!products.length) return;

    fetch("/cart.js", { signal: this._controller.signal })
      .then((res) => res.json())
      .then((cart) => {
        const cartProductIds = cart.items.map((item) => item.product_id);
        const filtered = products.filter((product) => !cartProductIds.includes(product.id));
        this.render(filtered.slice(0, 1));
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          console.warn("Erro no fetch do carrinho:", err);
        }
      });
  }

  disconnectedCallback() {
    this._connected = false;
    if (this._controller) this._controller.abort();
    this.innerHTML = "";
  }

  _parseProducts() {
    try {
      return JSON.parse(this.dataset.products || "[]");
    } catch (error) {
      console.warn("Produtos de upsell inválidos:", error);
      return [];
    }
  }

  _formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === "function" && window.money_format) {
      return window.Shopify.formatMoney(cents, window.money_format);
    }

    const value = Number(cents || 0) / 100;
    return value.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  }

  _productUrl(handle) {
    return `/products/${handle}`;
  }

  _getOptionName(option, index) {
    if (typeof option === "string") return option;
    return option?.name || `Opção ${index + 1}`;
  }

  _getOptionValues(product, option, index) {
    if (option?.values?.length) return option.values;
    return [...new Set(product.variants.map((variant) => variant.options[index]))];
  }

  _buildPrice(compareAt, price) {
    const wrapper = document.createElement("div");
    wrapper.className = "upsell-cart-item-price";

    if (compareAt && compareAt > price) {
      const compare = document.createElement("s");
      compare.textContent = this._formatMoney(compareAt);
      wrapper.appendChild(compare);
    }

    const current = document.createElement("strong");
    current.textContent = this._formatMoney(price);
    wrapper.appendChild(current);

    return wrapper;
  }

  _refreshPrice(priceWrapper, variant) {
    priceWrapper.replaceChildren(
      ...Array.from(this._buildPrice(variant.compare_at_price, variant.price).childNodes)
    );
  }

  render(products) {
    if (!products.length) return;

    products.forEach((product) => {
      fetch(`/products/${product.handle}.js`, {
        signal: this._controller.signal,
      })
        .then((res) => res.json())
        .then((data) => this._renderProduct(data))
        .catch((err) => {
          if (err.name !== "AbortError") {
            console.warn("Erro no fetch do produto:", err);
          }
        });
    });
  }

  _renderProduct(data) {
    if (!data?.variants?.length) return;

    const initialVariant = data.variants.find((variant) => variant.available) || data.variants[0];
    let selectedVariant = initialVariant;

    const itemWrapper = document.createElement("div");
    itemWrapper.className = "upsell-cart-item";

    const photoLink = document.createElement("a");
    photoLink.className = "upsell-cart-item-photo";
    photoLink.href = this._productUrl(data.handle);

    const img = document.createElement("img");
    img.src = data.featured_image;
    img.alt = data.title;
    img.loading = "lazy";
    photoLink.appendChild(img);

    const infoWrapper = document.createElement("div");
    infoWrapper.className = "upsell-cart-item-info";

    const top = document.createElement("div");
    top.className = "upsell-cart-item-top";

    const title = document.createElement("a");
    title.className = "upsell-cart-item-title";
    title.href = this._productUrl(data.handle);
    title.textContent = data.title;

    const priceWrapper = this._buildPrice(initialVariant.compare_at_price, initialVariant.price);

    top.appendChild(title);
    top.appendChild(priceWrapper);
    infoWrapper.appendChild(top);

    const form = document.createElement("form");
    form.className = "variant-selector";
    form.dataset.productId = data.id;

    const selectRow = document.createElement("div");
    selectRow.className = "select-row";

    const selects = [];
    const hasRealVariants = data.variants.length > 1 || !/default/i.test(initialVariant.title || "");

    if (hasRealVariants && data.options?.length) {
      data.options.forEach((option, index) => {
        const group = document.createElement("div");
        group.className = "select-group";

        const label = document.createElement("label");
        label.className = "upsell-visually-hidden";
        label.textContent = this._getOptionName(option, index);

        const select = document.createElement("select");
        select.dataset.index = index;
        select.setAttribute("aria-label", this._getOptionName(option, index));

        this._getOptionValues(data, option, index).forEach((value) => {
          const opt = document.createElement("option");
          opt.value = value;
          opt.textContent = value;
          select.appendChild(opt);
        });

        if (initialVariant.options[index]) {
          select.value = initialVariant.options[index];
        }

        selects.push(select);
        group.appendChild(label);
        group.appendChild(select);
        selectRow.appendChild(group);
      });
    }

    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "upsell-add-button";
    btn.textContent = "+";
    btn.setAttribute("aria-label", `Adicionar ${data.title} ao carrinho`);

    const msg = document.createElement("p");
    msg.className = "stock-message";

    const updateSelection = () => {
      if (selects.length) {
        const selectedOptions = selects.map((select) => select.value);
        selectedVariant = data.variants.find((variant) =>
          variant.options.every((option, index) => option === selectedOptions[index])
        );
      }

      if (selectedVariant) this._refreshPrice(priceWrapper, selectedVariant);

      const available = Boolean(selectedVariant && selectedVariant.available);
      btn.disabled = !available;
      msg.textContent = available ? "" : selectedVariant ? "Esgotado" : "Variação indisponível";
    };

    selects.forEach((select) => select.addEventListener("change", updateSelection));
    updateSelection();

    selectRow.classList.add(`select-row--${selects.length}-selects`);
    selectRow.appendChild(btn);
    form.appendChild(selectRow);
    form.appendChild(msg);

    btn.addEventListener("click", () => {
      if (!selectedVariant || !selectedVariant.available) return;

      btn.disabled = true;
      btn.classList.add("is-loading");
      btn.textContent = "...";
      msg.textContent = "";

      fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ id: selectedVariant.id, quantity: 1 }),
      })
        .then((res) => {
          if (!res.ok) throw new Error("Não foi possível adicionar o produto.");
          return res.json();
        })
        .then(() => {
          if (
            window.Shopify &&
            typeof window.Shopify.getCart === "function" &&
            window.sharedFunctions &&
            typeof window.sharedFunctions.updateSidebarCart === "function"
          ) {
            window.Shopify.getCart((cart) => {
              document.body.classList.add("cart-sidebar-show");
              window.sharedFunctions.updateSidebarCart(cart);
            });
          } else {
            window.location.reload();
          }
        })
        .catch((error) => {
          btn.disabled = false;
          btn.classList.remove("is-loading");
          btn.textContent = "+";
          msg.textContent = error.message || "Tente novamente.";
        });
    });

    infoWrapper.appendChild(form);
    itemWrapper.appendChild(photoLink);
    itemWrapper.appendChild(infoWrapper);

    this._container.appendChild(itemWrapper);
  }
}

if (!customElements.get("upsell-cart")) {
  customElements.define("upsell-cart", UpsellCart);
}
